/*
 * Change class built for redirecting browser to different URL
 * @module
 */

var _ = require('optimizely-client-fn');
var PromiseEnv = require('core/lib/promise').Promise;

var actionTypes = require('core/constants/action_types');
var ApiEvents = require('api/api_events');
var constantsEnums = require('core/constants/enums');
var Cookie = require('core/utils/cookie');
var DocumentEnv = require('core/env/document');
var enums = require('core/action/enums');
var Flux = require('core/flux');
var LocationEnv = require('core/utils/location');
var Logger = require('core/utils/logger');
var WindowEnv = require('core/env/window');

/**
 * @constructor
 *
 * @param {Change} change
 * @param {ChangeConfig} config
 * @throws {Error} if misconfigured.
 */
function RedirectChange (change:Change, config:ChangeConfig) {
  this.change = change;
  this.config = config;

  this.reasonShouldNotRedirect = this._checkForReasonNotToRedirect();
  if (this.reasonShouldNotRedirect) {
    return;
  }

  if (!_.isString(this.change.dest) && !_.isFunction(this.change.dest)) {
    throw new Error('Redirect destination is not a string or function');
  } else if (_.isFunction(this.change.dest)) {
    // Execute a function to determine URL. Call it now to see if it throws an error that gets in the way of
    // ANNOUNCE_PENDING_REDIRECT, but save its return value so we don't have to call the function again in .apply
    // (important because the function may have non-idempotent side effects or return value).
    this.change.dest = this.change.dest();
    if (!_.isString(this.change.dest)) {
      throw new Error('Redirect destination function did not evaluate to a string');
    }
  }
  if (!this.config.action) {
    throw new Error('Redirect changes require an action to be passed in the config.');
  }

  // Announce the pending redirect so that the we know to perform pre-redirect tracking
  // instead of non-redirect tracking.  (Tracking occurs before we apply the redirect change.)
  Flux.dispatch(actionTypes.ANNOUNCE_PENDING_REDIRECT, {
    layerId: this.config.action.layerId,
  });
}

/**
 * Do redirect
 * @returns {Promise}
 */
RedirectChange.prototype.apply = function apply(): Promise<any> {
  if (this.reasonShouldNotRedirect) {
    Logger.warn('Redirect change is not being applied because: ' + this.reasonShouldNotRedirect);
    return PromiseEnv.resolve(enums.changeState.IGNORED);
  }

  if (this.reasonInvalid) {
    Logger.error('Redirect change cannot be applied because:', this.reasonInvalid);
    return PromiseEnv.reject(this.reasonInvalid);
  }

  try {
    Flux.dispatch(actionTypes.SET_TRACKER_USE_BEACON, true);
    ApiEvents.emitMaybeSendEvents();
    Flux.dispatch(actionTypes.SET_TRACKER_USE_BEACON, false);
    this.__doRedirect(this.__createURL());
  } catch (err) {
    return PromiseEnv.reject(err);
  }

  return PromiseEnv.resolve(enums.changeState.APPLIED);
};

/**
 * Helper function to create URL for redirect - add schema and optional parameters
 *
 * @param {string|Function} url URL to redirect to
 * @returns {string}
 * @throws {Error} If url is a function and throws exception or if URL cannot be parsed.
 * @private
 */
RedirectChange.prototype.__createURL = function createURL(): string {
  // `this.change.dest` is definitely a string by now, thanks to the preprocessing in the constructor.
  var location = LocationEnv.create(this.change.dest);
  if (this.change.preserveParameters) {
    var currentParams = WindowEnv.getLocation().search;
    if (currentParams) {
      if (location.search) {
        // "merge" currentParams into location by append
        location.search += '&' + currentParams.substr(1);
      } else {
        // "replace" location params with currentParams
        location.search = currentParams;
      }
    }
  }

  return location.toString();
};

/**
 * Chrome has a problem where you see JSON objects or unstyled DOM objects before doing a redirect. (BUG-541)
 * Hiding body makes it look better. This needs to be done as a style in head because the flash still happens
 * if we wait until we can access the body element.
 * @private
 */
RedirectChange.prototype.__hidePage = function hidePage() {
  var style = DocumentEnv.createElement('style');
  style.setAttribute('type', 'text/css');
  style.innerHTML = 'body{display:none;visibility:hidden;}';
  DocumentEnv.appendToHead(style);
};

/**
 * Redirect page
 * @private
 */
RedirectChange.prototype.__doRedirect = function doRedirect(url) {
  var shouldHide = this.change.hasOwnProperty('hidePage') ? this.change.hidePage : true;
  if (shouldHide) {
    this.__hidePage();
  }

  // Do redirect
  WindowEnv.setLocation(url);
};

/**
 * Determines if redirect needs to happen in given conditions
 *
 * @returns {string|undefined} A reason not to redirect, if any is known.
 *
 * TODO(tyler): update tests to not require this to be exported
 * @private
 */
RedirectChange.prototype._checkForReasonNotToRedirect = function okToRedirect(): string|undefined {
  var redirectData = Cookie.get(constantsEnums.COOKIES.REDIRECT);
  if (!this.change.allowAdditionalRedirect && !_.isUndefined(redirectData)) {
    // Do not allow to redirect to happen if page was redirected already
    return 'Page was already redirected.';
  }
};

if (__UNDO_CHANGES_UPON_VIEW_DEACTIVATION__ || __TEST__) {
  /**
   * Once started - redirect cannot be undone.
   * Do resolve with IGNORE so that it doesn't get run again.
   * @returns {Promise}
   */
  RedirectChange.prototype.undo = function undo(): Promise<any> {
    return PromiseEnv.resolve(enums.changeState.IGNORED);
  };
}

/**
 * PluginManager registration method
 * @param {PluginManager} PluginManager
 */
module.exports = function(PluginManager:PluginManagerClass) {
  PluginManager.registerChangeApplier(enums.changeType.REDIRECT, RedirectChange);
};

if (__TEST__) {
  module.exports.plugin = RedirectChange;
}
